import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { MembroDaEquipe } from '../../acesso';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import { periodoDoTipo } from '../../design/graficos/ComPeriodo';
import { diaLongo, maisDias, pctTexto } from '../../paineis';
import type { Periodo } from '../../paineis';
import './visao-geral.css';
import { semanaAnterior, semanaDoDia } from '../../design/semana';

/**
 * PEÇAS DAS VISÕES (Visão geral e Vendas): o filtro de período no endereço, a barra de filtros,
 * o KPI com a trilha do período e o bloco com título. Uma peça só, o mesmo jeito nas duas telas.
 */

export type Filtro =
  | 'hoje'
  | 'ontem'
  | 'semana'
  | 'semana_passada'
  | 'mes'
  | 'mes_passado'
  | 'trimestre'
  | 'ano'
  | 'livre';
export const FILTROS: readonly [Filtro, string][] = [
  ['hoje', 'Hoje'],
  ['ontem', 'Ontem'],
  ['semana', 'Semana'],
  ['mes', 'Mês'],
  ['trimestre', 'Trimestre'],
  ['ano', 'Ano'],
  ['livre', 'Período'],
];
/**
 * O filtro das telas dos programas (vitamina, pré-vencido, paradão) — todas com os mesmos cinco
 * botões (dono, 05/10). A semana é a do cheque: sábado a sexta.
 */
export const FILTROS_DOS_PROGRAMAS: readonly [Filtro, string][] = [
  ['semana', 'Esta semana'],
  ['semana_passada', 'Semana passada'],
  ['mes', 'Este mês'],
  ['mes_passado', 'Mês passado'],
  ['livre', 'Período'],
];
const TODOS_OS_FILTROS: readonly Filtro[] = [
  ...FILTROS.map(([f]) => f),
  'semana_passada',
  'mes_passado',
];
export type OpcoesDoFiltro = {
  /** O filtro de quando o endereço não diz nenhum (o que não vai para o endereço). */
  readonly padrao?: Filtro;
  /**
   * Telas de validade olham para frente: a semana e o mês vão até o fim (não param em hoje) e o
   * período livre pode passar de hoje.
   */
  readonly futuro?: boolean;
};
const ehDia = (s: string | null): s is string => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

/** Os filtros saem do endereço (#/visao-geral?p=mes&pessoa=…); `?dia=` antigo vira um dia só. */
export function lerFiltros(q: URLSearchParams, hoje: string, opcoes: OpcoesDoFiltro = {}) {
  const padrao = opcoes.padrao ?? 'hoje';
  const futuro = opcoes.futuro ?? false;
  const pessoa = q.get('pessoa');
  const dia = q.get('dia');
  const p = q.get('p') as Filtro | null;
  let filtro: Filtro = TODOS_OS_FILTROS.some((f) => f === p) ? (p as Filtro) : padrao;
  let periodo: Periodo;
  if (ehDia(dia) && dia <= hoje && !p) {
    filtro = dia === hoje ? 'hoje' : 'livre';
    periodo = { de: dia, ate: dia };
  } else if (filtro === 'livre') {
    const de = q.get('de');
    const ate = q.get('ate');
    periodo =
      ehDia(de) && ehDia(ate) && de <= ate && (futuro || ate <= hoje)
        ? { de, ate }
        : { de: maisDias(hoje, -29), ate: hoje };
  } else periodo = periodoDoFiltro(filtro, hoje, futuro);
  return { filtro, periodo, pessoa: pessoa && /^[0-9a-f-]{36}$/.test(pessoa) ? pessoa : null };
}

const ultimoDiaDoMes = (mes: string) => {
  const [a, m] = mes.split('-').map(Number);
  return new Date(Date.UTC(a ?? 2026, m ?? 1, 0)).toISOString().slice(0, 10);
};

/** O intervalo de cada botão. A semana é a do cheque (sábado a sexta desde 24/10/2026). */
export function periodoDoFiltro(
  filtro: Exclude<Filtro, 'livre'>,
  hoje: string,
  futuro = false,
): Periodo {
  if (filtro === 'hoje') return { de: hoje, ate: hoje };
  if (filtro === 'ontem') return { de: maisDias(hoje, -1), ate: maisDias(hoje, -1) };
  const semana = periodoDoTipo('semana', hoje);
  if (filtro === 'semana') return futuro ? semanaDoDia(hoje) : semana;
  if (filtro === 'semana_passada') return semanaAnterior(hoje);
  if (filtro === 'mes') {
    const de = `${hoje.slice(0, 7)}-01`;
    return { de, ate: futuro ? ultimoDiaDoMes(hoje.slice(0, 7)) : hoje };
  }
  if (filtro === 'mes_passado') {
    const ate = maisDias(`${hoje.slice(0, 7)}-01`, -1);
    return { de: `${ate.slice(0, 7)}-01`, ate };
  }
  return periodoDoTipo(filtro, hoje);
}

export function irPara(
  f: { filtro: Filtro; periodo?: Periodo | undefined; pessoa: string | null },
  rota = 'visao-geral',
  /** Outros filtros da tela (tipo, busca…) que continuam no endereço. */
  extras: Readonly<Record<string, string | null | undefined>> = {},
  /** O filtro que não precisa ir para o endereço (o padrão da tela). */
  padrao: Filtro = 'hoje',
) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(extras)) if (v) q.set(k, v);
  if (f.filtro !== padrao) q.set('p', f.filtro);
  if (f.filtro === 'livre' && f.periodo) {
    q.set('de', f.periodo.de);
    q.set('ate', f.periodo.ate);
  }
  if (f.pessoa) q.set('pessoa', f.pessoa);
  const s = q.toString();
  window.location.hash = s ? `#/${rota}?${s}` : `#/${rota}`;
}

export const diaCurto = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
export const soma = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);
export const variacao = (a: number, b: number) =>
  b > 0 ? Math.round(((a - b) / b) * 1000) / 10 : null;
export const nomeCurto = (s: string) => {
  const p = s.trim().split(/\s+/);
  const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1).toLowerCase();
  return p.length > 1 ? `${cap(p[0] ?? '')} ${cap(p[p.length - 1] ?? '')}` : cap(p[0] ?? s);
};

export function textoDoPeriodo(filtro: Filtro, p: Periodo, hoje: string) {
  if (p.de === p.ate) return p.de === hoje ? 'hoje' : diaCurto(p.de);
  const rot = {
    semana: 'esta semana',
    semana_passada: 'semana passada',
    mes: 'este mês',
    mes_passado: 'mês passado',
    trimestre: 'este trimestre',
    ano: 'este ano',
  };
  return filtro in rot ? rot[filtro as keyof typeof rot] : `${diaCurto(p.de)} a ${diaCurto(p.ate)}`;
}

/* ------------------------------------------------------------------ *
 * Filtros: período e balconista (tudo no endereço)
 * ------------------------------------------------------------------ */

export function BarraDeFiltros({
  f,
  hoje,
  equipe,
  rota = 'visao-geral',
  extras = {},
  filtros = FILTROS,
  padrao = 'hoje',
  futuro = false,
  maxDias = 366,
  semFiltro,
  children,
}: {
  f: ReturnType<typeof lerFiltros>;
  hoje: string;
  /** Sem `equipe`, a barra não mostra o filtro de balconista. */
  equipe?: readonly MembroDaEquipe[] | undefined;
  rota?: string;
  /** Filtros próprios da tela, mantidos ao trocar o período. */
  extras?: Readonly<Record<string, string | null | undefined>>;
  /** Os botões de período (padrão: os da Visão geral). */
  filtros?: readonly (readonly [Filtro, string])[];
  /** O filtro de quando o endereço não diz nenhum — "Limpar filtros" volta para ele. */
  padrao?: Filtro;
  /** Período livre pode passar de hoje (telas de validade). */
  futuro?: boolean;
  /** Maior período livre aceito, em dias. */
  maxDias?: number;
  /**
   * Quando o padrão não é um dos botões, a tela começa SEM período (nenhum botão aceso) e a
   * barra mostra este texto no lugar das datas.
   */
  semFiltro?: string;
  /** Controles da tela, na mesma barra. */
  children?: ReactNode;
}) {
  const [livre, setLivre] = useState<Periodo>(f.periodo);
  useEffect(() => setLivre(f.periodo), [f.periodo.de, f.periodo.ate]);
  const longo = (Date.parse(livre.ate) - Date.parse(livre.de)) / 86_400_000 > maxDias;
  const valido = livre.de <= livre.ate && (futuro || livre.ate <= hoje) && !longo;
  const mudou = f.filtro !== padrao || f.pessoa || Object.values(extras).some(Boolean);
  const pessoas = [...(equipe ?? [])].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  return (
    <div className="vg-filtros" role="toolbar" aria-label="Filtros da visão geral">
      <div className="vg-segmentos" role="group" aria-label="Período">
        {filtros.map(([id, rotulo]) => (
          <button
            key={id}
            type="button"
            className={`vg-seg${f.filtro === id ? ' vg-seg-ativo' : ''}`}
            aria-pressed={f.filtro === id}
            onClick={() =>
              irPara(
                {
                  filtro: id,
                  periodo: id === 'livre' ? f.periodo : undefined,
                  pessoa: f.pessoa,
                },
                rota,
                extras,
                padrao,
              )
            }
          >
            {rotulo}
          </button>
        ))}
      </div>
      {f.filtro === 'livre' && (
        <form
          className="vg-livre"
          onSubmit={(e) => {
            e.preventDefault();
            if (valido)
              irPara({ filtro: 'livre', periodo: livre, pessoa: f.pessoa }, rota, extras, padrao);
          }}
        >
          <input
            type="date"
            value={livre.de}
            max={livre.ate}
            aria-label="De"
            onChange={(e) => setLivre((p) => ({ ...p, de: e.target.value }))}
          />
          <span>a</span>
          <input
            type="date"
            value={livre.ate}
            min={livre.de}
            max={futuro ? undefined : hoje}
            aria-label="Até"
            onChange={(e) => setLivre((p) => ({ ...p, ate: e.target.value }))}
          />
          <button type="submit" className="vg-aplicar" disabled={!valido}>
            Aplicar
          </button>
          {longo && <em>{maxDias >= 366 ? 'no máximo 1 ano' : `no máximo ${maxDias} dias`}</em>}
        </form>
      )}
      {equipe && (
        <label className="vg-pessoa">
          <Icone nome="users" tamanho={14} traco={1.6} />
          <select
            value={f.pessoa ?? ''}
            aria-label="Balconista"
            onChange={(e) => irPara({ ...f, pessoa: e.target.value || null }, rota, extras, padrao)}
          >
            <option value="">Toda a loja</option>
            {pessoas.map((p) => (
              <option key={p.accountId} value={p.accountId ?? ''}>
                {nomeCurto(p.nome)}
                {p.login ? ` · ${p.login}` : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      {children}
      <span className="vg-periodo-texto">
        {semFiltro && f.filtro === padrao
          ? semFiltro
          : f.periodo.de === f.periodo.ate
            ? diaLongo(f.periodo.de)
            : `${diaCurto(f.periodo.de)}/${f.periodo.de.slice(0, 4)} a ${diaCurto(f.periodo.ate)}/${f.periodo.ate.slice(0, 4)}`}
      </span>
      {mudou && (
        <button
          type="button"
          className="vg-limpar"
          onClick={() => irPara({ filtro: padrao, pessoa: null }, rota, {}, padrao)}
        >
          Limpar filtros
        </button>
      )}
    </div>
  );
}

export const pctVar = (pct: number | null, base: string) =>
  pct === null
    ? null
    : { pct, base, tom: Math.abs(pct) < 0.05 ? 'neutro' : pct > 0 ? 'bom' : 'ruim' };

/* ------------------------------------------------------------------ *
 * KPI com a trilha do período (mini-gráfico)
 * ------------------------------------------------------------------ */

export function Kpi({
  rotulo,
  icone,
  valor,
  variacao: v,
  sub,
  trilha,
  barra,
  link,
  destaque,
}: {
  rotulo: string;
  icone: NomeDoIcone;
  /** `null` = ainda carregando. */
  valor: string | null;
  variacao: { pct: number; base: string; tom: string } | null;
  sub?: string | null;
  trilha?: readonly number[] | undefined;
  barra?: number | null;
  link: string;
  destaque?: boolean;
}) {
  return (
    <a className={`vg-kpi${destaque ? ' vg-kpi-destaque' : ''}`} href={link}>
      <div className="vg-kpi-rotulo">
        <span>{rotulo}</span>
        <Icone nome={icone} tamanho={15} traco={1.5} />
      </div>
      {valor === null ? (
        <div className="vg-kpi-carregando" aria-busy="true" />
      ) : (
        <>
          <div className="vg-kpi-valor">{valor}</div>
          <div className="vg-kpi-pe">
            {v ? (
              <span className={`vg-var vg-var-${v.tom}`} title={`Comparado com ${v.base}`}>
                {v.pct !== 0 && (
                  <span className={v.pct > 0 ? 'vg-seta' : 'vg-seta vg-seta-desce'}>
                    <Icone nome="up-t" tamanho={12} traco={2} />
                  </span>
                )}
                <strong>{pctTexto(v.pct)}</strong>
                <span className="vg-var-base">
                  {v.base.startsWith('vs') ? v.base : `vs ${v.base}`}
                </span>
              </span>
            ) : (
              sub && <span className="vg-kpi-sub">{sub}</span>
            )}
          </div>
          {barra !== undefined && barra !== null ? (
            <div className="vg-kpi-barra">
              <i style={{ width: `${String(Math.max(0, Math.min(100, barra)))}%` }} />
            </div>
          ) : (
            trilha && trilha.length > 1 && <Trilha valores={trilha} />
          )}
        </>
      )}
    </a>
  );
}

export function Trilha({ valores }: { valores: readonly number[] }) {
  const L = 200;
  const A = 34;
  const max = Math.max(...valores, 0.0001);
  const min = Math.min(...valores, 0);
  const x = (i: number) => (i / (valores.length - 1)) * L;
  const y = (v: number) => A - 3 - ((v - min) / (max - min || 1)) * (A - 6);
  const linha = valores.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg
      className="vg-trilha"
      viewBox={`0 0 ${String(L)} ${String(A)}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polygon
        points={`0,${String(A)} ${linha} ${String(L)},${String(A)}`}
        className="vg-trilha-area"
      />
      <polyline points={linha} className="vg-trilha-linha" />
    </svg>
  );
}

export function Bloco({
  titulo,
  sub,
  link,
  rotuloDoLink,
  children,
}: {
  titulo: string;
  sub: string;
  link?: string;
  rotuloDoLink?: string;
  children: ReactNode;
}) {
  return (
    <div className="vg-bloco">
      <div className="vg-bloco-topo">
        <div>
          <h2>{titulo}</h2>
          <p>{sub}</p>
        </div>
        {link && (
          <a href={link} className="vg-bloco-link">
            {rotuloDoLink}
            <Icone nome="chev-r" tamanho={13} traco={1.8} />
          </a>
        )}
      </div>
      {children}
    </div>
  );
}

export function Carregando() {
  return (
    <div className="vg-lista-osso" aria-busy="true">
      {[0, 1, 2, 3, 4].map((k) => (
        <i key={k} />
      ))}
    </div>
  );
}
